import { useMemo, useState, type CSSProperties } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { EventActions } from './EventActions'
import { EventSheet } from './EventSheet'
import { Banner, Checkbox, Chip, IconButton, Row, Segmented, useToast } from '../ui'
import { useCommitments, useEntryState, useFeed, useShowings } from '../lib/queries'
import { useDismissEntry, useForgetEntry, useMarkWent, useSaveEntry } from '../lib/mutations'
import { useSeen, type SeenRow } from '../lib/seen'
import { useWriteError } from '../lib/useAction'
import { fmt } from '../lib/calendar'
import { clockIn, dayIn, zoneOfCity } from '../lib/zone'
import { heroIsVenue, heroUrl, idsOf, posterUrl, signalFeatures, typeLabel } from '../lib/entries'
import { leadingOf, plateFor, rowTitle } from '../lib/rowMeta'
import { groupShortlist } from '../lib/shortlist'
import { needYouPhrase } from '../lib/attention'
import type { Commitment, FeedRow } from '../lib/types'
import type { ShowingLite } from './CardBody'
import { Loading, Failed } from '../screens/Stub'
import { userKey } from '../lib/userStorage'
import './EventRow.css'

/** One identity for "nothing yet", so the memos below do not recompute on a
 *  fresh empty array every render. */
const NONE_YET: never[] = []
const NONE_SHOWINGS: ShowingLite[] = []
/** `EventActions`'s "Save"/"Remove from saved" row reads `saved` and this
 *  body always passes `true` — every row here is definitionally saved — so
 *  its `onSave` branch is unreachable and this is what it points at. */
const noop = () => {}

type Tab = 'upcoming' | 'undated' | 'went'

const TABS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'undated', label: 'Undated' },
  { value: 'went', label: 'Went' },
]

/** The inline variant remembers its segment for the tab session, so leaving
 *  the Lists tab and coming back finds Went where it was left. `sessionStorage`
 *  and not `localStorage`: that one holds the signed-in session (CLAUDE.md
 *  rule 7) and this is a convenience that should die with the tab. Every
 *  access is guarded — it throws in a private window and with site data
 *  blocked, and the segment is then simply Upcoming each time. */
export const SHORTLIST_TAB_KEY = 'kiez.shortlist.tab'

function readTab(): Tab {
  try {
    const v = sessionStorage.getItem(userKey(SHORTLIST_TAB_KEY))
    return v === 'undated' || v === 'went' ? v : 'upcoming'
  } catch { return 'upcoming' }
}

function writeTab(tab: Tab) {
  try { sessionStorage.setItem(userKey(SHORTLIST_TAB_KEY), tab) } catch { /* the segment just is not remembered */ }
}

/**
 * Everything the Shortlist screen draws below its `ScreenHead` — the count
 * line, the Upcoming · Undated · Went segments, the grouped rows, Went's
 * checkbox, the empty states and the two sheets — as a component the Lists
 * index can mount as well as the screen at `#/lists/shortlist`.
 *
 * It reads its own queries rather than taking props, so a caller mounts it
 * with no plumbing; the cost is that the screen and the index each hold their
 * own `useState` for the open sheet and the segment, which is what they want.
 *
 * - `screen`: the sticky count line, as the screen has always drawn it.
 * - `inline`: the count line alone, left-aligned, under the chip row — no
 *   heading, because the selected "Shortlist" chip above it is the name —
 *   not sticky (the index scrolls the chip row above it away; a bar pinned
 *   to the top would sit over rows that belong to no list), and the segment
 *   is remembered.
 *
 * WP 4.3 gave the Shortlist its three tabs; `groupShortlist` decides which
 * a row is in and this only draws it. A saved event that needs you in the
 * next 48 hours leads its meta with when (`needYouPhrase`, Upcoming only).
 */
export function ShortlistBody({ variant }: { variant: 'screen' | 'inline' }) {
  const inline = variant === 'inline'
  const [open, setOpen] = useState<FeedRow | null>(null)
  const [menuFor, setMenuFor] = useState<FeedRow | null>(null)
  const [tab, setTabState] = useState<Tab>(() => (inline ? readTab() : 'upcoming'))
  const setTab = (t: Tab) => { setTabState(t); if (inline) writeTab(t) }
  const [wentKind, setWentKind] = useState<string | null>(null)
  // No query backs this: `markWent` writes only `taste_signal`, an
  // append-only log with nothing to read a "what did I answer" flag off. The
  // answer stays for the rest of this visit, which is enough to keep the
  // checkbox honest about which way it was last tapped; it does not survive
  // a reload, and nothing here pretends it does. A `Set` of "already
  // answered" ids would not do — unticking is itself an answer (`skipped`),
  // and a Set that only ever grew would show every unticked checkbox as
  // still ticked.
  const [answered, setAnswered] = useState<Map<string, boolean>>(() => new Map())

  const stateQ = useEntryState()
  const feedQ = useFeed()
  const commitmentsQ = useCommitments()
  const showingsQ = useShowings()
  // WP 8.4: the Went tab's own stars. Not folded into the pending/error
  // aggregate below — a seen row is a nicety on a row already drawn, not
  // something the tab has to wait for.
  const seenQ = useSeen()

  const stateRows = stateQ.data ?? NONE_YET
  const feed = feedQ.data ?? NONE_YET
  const commitments = commitmentsQ.data ?? NONE_YET

  const saveM = useSaveEntry()
  const forgetM = useForgetEntry()
  const dismissM = useDismissEntry()
  const markWentM = useMarkWent()
  const writeError = useWriteError(markWentM)
  const toast = useToast()

  const rows = useMemo(() => {
    const byId = new Map(feed.map(e => [e.id, e]))
    const savedIds = stateRows.filter(r => r.state === 'saved').map(r => r.entry_id)
    return savedIds.map(id => byId.get(id)).filter((e): e is NonNullable<typeof e> => !!e)
  }, [feed, stateRows])

  const groups = useMemo(
    () => groupShortlist(rows, showingsQ.data?.byEntry ?? new Map(), commitments, new Date()),
    [rows, showingsQ.data, commitments],
  )

  const wentKinds = useMemo(() => Array.from(new Set(groups.went.map(e => e.type))).sort(), [groups.went])
  const wentRows = wentKind ? groups.went.filter(e => e.type === wentKind) : groups.went

  const pending = stateQ.isPending || feedQ.isPending || commitmentsQ.isPending || showingsQ.isPending
  const error = stateQ.error ?? feedQ.error ?? commitmentsQ.error ?? showingsQ.error

  if (pending) return <Loading />
  if (error) return <Failed error={error.message} />

  // Rule 2: Unsave and Not for me are distinct writes, and both get an Undo —
  // `EventRow`/`EventSheet` already give the second one a toast; this screen
  // gives the first one the same, which those two do not, because unsaving
  // is the one action a screen full of saved rows can do by the dozen and a
  // stray tap here is a worse loss than it is a tap or two elsewhere.
  const forget = (e: FeedRow) => {
    const ids = idsOf(e)
    forgetM.mutate({ ids })
    toast({ text: 'Removed from the Shortlist', action: { label: 'Undo', onClick: () => saveM.mutate({ ids, features: signalFeatures(e) }) } })
  }
  const dismiss = (e: FeedRow) => {
    const ids = idsOf(e)
    dismissM.mutate({ ids, features: signalFeatures(e) })
    toast({ text: 'Hidden', action: { label: 'Undo', onClick: () => forgetM.mutate({ ids }) } })
  }

  const commitmentFor = (e: FeedRow): Commitment | null => {
    const ids = idsOf(e)
    return commitments.find(c => c.entry_id && ids.includes(c.entry_id)) ?? null
  }

  const showingsOf = (e: FeedRow): ShowingLite[] => {
    const byEntry = showingsQ.data?.byEntry
    if (!byEntry) return NONE_SHOWINGS
    return idsOf(e).flatMap(id => byEntry.get(id) ?? [])
  }

  /** Rule 1: only ever called from the checkbox's own `onChange`, never from
   *  a render effect or a schedule — there is no path from "14 days have
   *  passed" to this function. */
  const tick = (e: FeedRow, went: boolean) => {
    markWentM.mutate(
      { entryId: e.id, went, features: signalFeatures(e) },
      { onSuccess: () => setAnswered(prev => new Map(prev).set(e.id, went)) },
    )
  }

  const now = new Date()
  const upcomingFact = (e: FeedRow & { calendarStatus: 'scheduled' | 'pick' | null }): RowFact => ({
    when: needYouPhrase(e, now),
    status: e.calendarStatus === 'scheduled' ? { text: 'in the Year', tone: 'ok' }
      : e.calendarStatus === 'pick' ? { text: 'pick a night', tone: 'warn' } : null,
  })

  const countLine = (
    <span className="count">
      <b>{rows.length}</b> saved
      {groups.undated.length > 0 && (
        <span className="shortlist__needsdate"> · {groups.undated.length} undated</span>
      )}
    </span>
  )

  return (
    <>
      {writeError && <Banner tone="bad">{writeError}</Banner>}

      {rows.length === 0 ? (
        <>
          <div className="empty">Nothing saved yet. Swipe right in the feed and it lands here.</div>
        </>
      ) : (
        <>
          {inline
            ? <div className="shortlist__head">{countLine}</div>
            : <div className="stickybar">{countLine}</div>}

          <Segmented
            className={`shortlist__tabs${inline ? ' shortlist__tabs--fill' : ''}`}
            value={tab}
            onChange={v => setTab(v as Tab)}
            options={TABS}
          />

          {tab === 'upcoming' && (
            groups.upcoming.length === 0 ? (
              <div className="empty">Nothing ahead — everything saved is undated, or has already passed.</div>
            ) : groups.upcoming.map(g => (
              <div key={g.heading}>
                {/* `DayHeading` takes a calendar day; a week or a month label
                    is not one, so this draws the same `.rowhead` markup
                    directly rather than bending that component's signature
                    to fit a heading it was never asked to format. */}
                <div className="rowhead"><h2>{g.heading}</h2><span className="g">{g.rows.length}</span></div>
                {g.rows.map(e => (
                  <ShortlistRow
                    key={e.id} e={e} fact={upcomingFact(e)}
                    onOpen={() => setOpen(e)} onMenu={() => setMenuFor(e)}
                  />
                ))}
              </div>
            ))
          )}

          {tab === 'undated' && (
            groups.undated.length === 0 ? (
              <div className="empty">Nothing undated — every saved event has a night, or several to choose from.</div>
            ) : groups.undated.map(e => (
              <ShortlistRow
                key={e.id} e={e} fact={{ when: null, status: { text: 'pick a night', tone: 'warn' } }}
                onOpen={() => setOpen(e)} onMenu={() => setMenuFor(e)}
              />
            ))
          )}

          {tab === 'went' && (
            <>
              {/* WP 8.4 mounted `RateQueue` here, only so it could be rendered
                  and exercised by that package; WP 8.5 moved it to the top of
                  the Year, which is where Doug actually asked for it. */}
              {wentKinds.length > 1 && (
                <div className="shortlist__kinds">
                  <Chip on={wentKind === null} onClick={() => setWentKind(null)}>All</Chip>
                  {wentKinds.map(k => (
                    <Chip key={k} on={wentKind === k} onClick={() => setWentKind(k)}>{typeLabel(k)}</Chip>
                  ))}
                </div>
              )}
              {wentRows.length === 0 ? (
                <div className="empty">Nothing here yet — a saved event moves here once its date has passed.</div>
              ) : wentRows.map((e, i) => (
                <WentRow
                  key={e.id} e={e} position={i + 1}
                  seen={seenQ.data?.get(e.id) ?? null}
                  checked={answered.get(e.id) ?? false}
                  onChange={went => tick(e, went)}
                  onOpen={() => setOpen(e)} onMenu={() => setMenuFor(e)}
                />
              ))}
            </>
          )}
        </>
      )}

      <EventSheet e={open} onClose={() => setOpen(null)} />
      {menuFor && (
        <EventActions
          e={menuFor}
          saved
          showings={showingsOf(menuFor)}
          commitment={commitmentFor(menuFor)}
          summary={null}
          onClose={() => setMenuFor(null)}
          onSave={noop}
          onForget={() => forget(menuFor)}
          onDismiss={() => dismiss(menuFor)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The rows
// ---------------------------------------------------------------------------

/** The thumbnail, copied from `EventRow.tsx`'s own `Leader` rather than
 *  imported — that component is on the brief's do-not-touch list, and its
 *  ref/view-transition plumbing is for the feed's shared-element open, which
 *  this screen's simpler tap does not do. Failing image just leaves the plate
 *  showing under it (no retry): this is a secondary screen, not the feed. */
function Thumb({ e }: { e: FeedRow }) {
  const now = new Date()
  const kind = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  return (
    <span className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as CSSProperties) : undefined}>
      {plate && (
        <span className="ev-plate">
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img src={kind.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
      )}
    </span>
  )
}

/** A row's second line, as two independent facts. `when` is the need-you lead
 *  (inside the 48-hour window only) and `status` is the calendar status; they
 *  are separate so a row with a lead and no status — one date, nothing to
 *  pick — still shows the lead, and neither decides whether the other draws. */
interface RowFact {
  when: string | null
  status: { text: string; tone: 'ok' | 'warn' } | null
}

/** Upcoming and Undated share this anatomy: the calendar status is the fact
 *  line (rule: never the row's own `metaLine`, which knows nothing about a
 *  commitment), and the trailing slot opens the same six-item menu every
 *  other row in the app opens — Calendar, Share, Unsave, Not for me among
 *  them — rather than the heart-toggle swipe a row you have not saved yet
 *  gets. */
function ShortlistRow({ e, fact, onOpen, onMenu }: {
  e: FeedRow
  fact: RowFact
  onOpen: () => void
  onMenu: () => void
}) {
  const meta = fact.when || fact.status ? (
    <>
      {fact.when && <span className="shortlist__when">{fact.when}</span>}
      {fact.when && fact.status && ' · '}
      {fact.status && <span className={`shortlist__fact shortlist__fact--${fact.status.tone}`}>{fact.status.text}</span>}
    </>
  ) : null
  return (
    <Row
      leading={<Thumb e={e} />}
      title={rowTitle(e)}
      meta={meta}
      trailing={
        <IconButton label={`More on ${rowTitle(e)}`} onClick={onMenu}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </IconButton>
      }
      onPress={onOpen}
    />
  )
}

/** `Thu 5 Nov 2026`, or with a real clocked time — the same rule
 *  `ShowingChips`' own `label` uses, restated with the year because Went can
 *  hold a date from a different one. A timestamp is read in `tz`, the row's
 *  own zone (C2.1). */
function whenLabel(when: string, tz: string): string {
  if (!when.includes('T')) return fmt(when, { day: 'numeric', month: 'short', year: 'numeric' })
  const day = fmt(dayIn(when, tz), { day: 'numeric', month: 'short', year: 'numeric' })
  const time = clockIn(when, tz)
  return time === '00:00' ? day : `${day} · ${time}`
}

/** WP 8.4: `★★★★☆`, or "seen" for a row marked seen with no rating — the same
 *  two-line shape `EventActions`' own menu row reads off a `SeenRow`. */
function starsLabel(seen: SeenRow | null): string | null {
  if (!seen) return null
  return seen.stars != null ? '★'.repeat(seen.stars) + '☆'.repeat(5 - seen.stars) : 'seen'
}

/** Went: the record. A long press still reaches the same menu Upcoming's
 *  IconButton opens (Share, a note, Unsave) — the checkbox is the one new
 *  control this tab has, and it does not need a second trailing slot beside
 *  it to get the other five. */
function WentRow({ e, position, seen, checked, onChange, onOpen, onMenu }: {
  e: FeedRow
  position: number
  /** WP 8.4: this row's own `seen` row, where Doug has rated it. */
  seen: SeenRow | null
  checked: boolean
  onChange: (went: boolean) => void
  onOpen: () => void
  onMenu: () => void
}) {
  const when = e.next_showing ?? e.run_end ?? e.run_start
  const meta = [when ? whenLabel(when, zoneOfCity(e.city)) : null, starsLabel(seen)].filter(Boolean).join(' · ') || null
  return (
    <Row
      leading={<Thumb e={e} />}
      title={rowTitle(e)}
      meta={meta}
      trailing={
        <Checkbox
          shape="round"
          checked={checked}
          onChange={onChange}
          aria-label={`Went? ${rowTitle(e)}, row ${position}`}
        />
      }
      onPress={onOpen}
      onLongPress={onMenu}
    />
  )
}
